import { ApolloClient, HttpLink, from } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';
import { ssoRedirect } from 'src/utils/ssoRedirect';
import { customFetch } from 'src/utils/customFetch';
import { getUri } from 'src/utils/getUri';
import {
  appModeUuid,
  defaultLanguage,
  isOnboardingTemporarilyDisabled,
} from 'src/utils/capabilities';
import { getBaseUrl } from 'src/utils/getConfig';
import {
  UNAUTHORIZED_PAGE_URL,
  FORBIDDEN_PAGE_URL,
  DEPRECATED_FEATURE_PAGE,
  ONBOARDING_PAGE_URL,
} from 'src/constants/urlConstants';
import { apolloCache } from './apolloCache';

const uri = getUri();
const abortController = new AbortController();
const UNAUTHORIZED_AGENCY = 'Unauthorized Agency';
const GET_SESSION_QUERY = 'GetSessionQuery';

const headers = {
  'x-applicationmode': appModeUuid,
  'x-language': defaultLanguage,
};

const baseUrl = getBaseUrl();

const errorLink = onError(onErrorArgs => {
  const { graphQLErrors = [], response = {}, operation = {} } = onErrorArgs;
  const [graphQLError] = graphQLErrors;
  const is401Page = window.location.pathname.includes(UNAUTHORIZED_PAGE_URL);
  const isForbiddenPage = window.location.pathname.includes(FORBIDDEN_PAGE_URL);
  const is410Page = window.location.pathname.includes(DEPRECATED_FEATURE_PAGE);
  const isErrorPage = is401Page || isForbiddenPage || is410Page;

  if (graphQLError) {
    const { extensions, message } = graphQLError;

    const errorCodes = graphQLErrors.map(err => err?.extensions?.code);
    const isUnauthorizedUser =
      extensions?.code === 401 ||
      (graphQLErrors.length && errorCodes.includes(401));

    if (message === UNAUTHORIZED_AGENCY && !isErrorPage) {
      apolloClient.clearStore();
      window.location.assign(`${baseUrl}${FORBIDDEN_PAGE_URL}`);
      response.errors = null;
    } else if (
      window.location.pathname.includes(ONBOARDING_PAGE_URL) &&
      isOnboardingTemporarilyDisabled
    ) {
      window.location.assign(`${baseUrl}${DEPRECATED_FEATURE_PAGE}`);
    } else if (isUnauthorizedUser && !isErrorPage) {
      if (operation.operationName === GET_SESSION_QUERY) {
        ssoRedirect.login({ internal: false });
      } else {
        window.location.assign(`${baseUrl}${UNAUTHORIZED_PAGE_URL}`);
      }
    }
  }
});

const httpLink = new HttpLink({
  uri,
  fetchOptions: {
    mode: 'cors',
    signal: abortController.signal,
  },
  fetch: customFetch,
  headers,
});

const retryLink = new RetryLink();

const link = from([retryLink, errorLink, httpLink]);

export const apolloClient = new ApolloClient({
  cache: apolloCache,
  credentials: 'same-origin',
  link,
});
